import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { CheckCircle, XCircle, Clock, FileCheck, Loader2, User, MessageSquare, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn, formatDateTime } from '../lib/utils';

export default function Approvals() {
  const [approvals, setApprovals] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [actionType, setActionType] = useState('approve');
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [allRes, pendingRes] = await Promise.all([
        api.get('/approvals'),
        api.get('/approvals/pending')
      ]);
      setApprovals(allRes.data);
      setPendingApprovals(pendingRes.data);
    } catch (error) {
      toast.error('Erro ao carregar aprovações');
    } finally {
      setIsLoading(false);
    }
  };

  const openActionDialog = (approval, type) => {
    setSelectedApproval(approval);
    setActionType(type);
    setComments('');
    setShowDialog(true);
  };

  const handleAction = async () => {
    if (!selectedApproval) return;
    
    setIsSubmitting(true);
    try {
      const endpoint = actionType === 'approve' 
        ? `/approvals/${selectedApproval.approval_id}/approve`
        : `/approvals/${selectedApproval.approval_id}/reject`;
      
      await api.put(endpoint, null, { params: { comments } });
      toast.success(actionType === 'approve' ? 'Pedido aprovado' : 'Pedido rejeitado');
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar pedido');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      aprovado: { label: 'Aprovado', className: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
      rejeitado: { label: 'Rejeitado', className: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
      submetido: { label: 'Submetido', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock },
      em_revisao: { label: 'Em Revisão', className: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock },
      pendente_aprovacao: { label: 'Pendente', className: 'bg-orange-50 text-orange-700 border-orange-200', icon: Clock }
    };
    const config = configs[status] || configs.submetido;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={cn('text-xs flex items-center gap-1', config.className)}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getEntityTypeName = (type) => {
    const names = {
      asset: 'Ativo',
      incident: 'Incidente',
      risk: 'Risco',
      task: 'Tarefa'
    };
    return names[type] || type;
  };

  const getActionName = (action) => {
    const names = {
      create: 'Criação',
      update: 'Atualização',
      delete: 'Eliminação',
      status_change: 'Alteração de Estado'
    };
    return names[action] || action;
  };

  if (isLoading) {
    return (
      <Layout title="Aprovações">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Workflow de Aprovações" headerContent={
      <Button variant="outline" onClick={loadData}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Atualizar
      </Button>
    }>
      <div className="space-y-6" data-testid="approvals-container">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-flat">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <FileCheck className="h-8 w-8 text-slate-400" />
                <span className="text-2xl font-bold text-slate-900">{approvals.length}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Total</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Clock className="h-8 w-8 text-orange-500" />
                <span className="text-2xl font-bold text-orange-600">{pendingApprovals.length}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Pendentes</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <span className="text-2xl font-bold text-green-600">
                  {approvals.filter(a => a.status === 'aprovado').length}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Aprovados</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <XCircle className="h-8 w-8 text-red-500" />
                <span className="text-2xl font-bold text-red-600">
                  {approvals.filter(a => a.status === 'rejeitado').length}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Rejeitados</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Approvals */}
        {pendingApprovals.length > 0 && (
          <Card className="card-flat border-l-4 border-l-orange-500">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-500" />
                Aprovações Pendentes ({pendingApprovals.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingApprovals.map((approval) => (
                <div key={approval.approval_id} className="flex items-center justify-between bg-orange-50 p-4 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-slate-500">{approval.approval_id}</span>
                      <Badge variant="outline" className="text-xs">{getEntityTypeName(approval.entity_type)}</Badge>
                      <Badge variant="outline" className="text-xs">{getActionName(approval.action)}</Badge>
                    </div>
                    <p className="text-sm text-slate-600">
                      Solicitado por: <strong>{approval.requester_name}</strong>
                    </p>
                    {approval.justification && (
                      <p className="text-sm text-slate-500 mt-1">"{approval.justification}"</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => openActionDialog(approval, 'reject')}>
                      <XCircle className="h-4 w-4 mr-1" />
                      Rejeitar
                    </Button>
                    <Button size="sm" onClick={() => openActionDialog(approval, 'approve')}>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Aprovar
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* All Approvals */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg">Histórico de Aprovações</CardTitle>
          </CardHeader>
          <CardContent>
            {approvals.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileCheck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>Nenhum pedido de aprovação</p>
              </div>
            ) : (
              <div className="space-y-3">
                {approvals.map((approval) => (
                  <div key={approval.approval_id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono text-xs text-slate-500">{approval.approval_id}</span>
                          {getStatusBadge(approval.status)}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Badge variant="outline">{getEntityTypeName(approval.entity_type)}</Badge>
                          <Badge variant="outline">{getActionName(approval.action)}</Badge>
                          <span className="text-slate-400">•</span>
                          <span className="text-slate-500">{approval.entity_id}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-2">
                          <User className="h-3 w-3 inline mr-1" />
                          {approval.requester_name} • {formatDateTime(approval.created_at)}
                        </p>
                        {approval.justification && (
                          <p className="text-sm text-slate-500 mt-1 italic">
                            <MessageSquare className="h-3 w-3 inline mr-1" />
                            {approval.justification}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Approval Steps */}
                    {approval.approval_steps?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <p className="text-xs text-slate-500 mb-2">Aprovadores:</p>
                        <div className="flex flex-wrap gap-2">
                          {approval.approval_steps.map((step, idx) => (
                            <div key={idx} className={cn(
                              'text-xs px-2 py-1 rounded flex items-center gap-1',
                              step.status === 'aprovado' ? 'bg-green-100 text-green-700' :
                              step.status === 'rejeitado' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            )}>
                              {step.status === 'aprovado' ? <CheckCircle className="h-3 w-3" /> :
                               step.status === 'rejeitado' ? <XCircle className="h-3 w-3" /> :
                               <Clock className="h-3 w-3" />}
                              {step.approver_name || step.approver_id}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? '✅ Aprovar Pedido' : '❌ Rejeitar Pedido'}
            </DialogTitle>
            <DialogDescription>
              {selectedApproval?.approval_id} - {getEntityTypeName(selectedApproval?.entity_type)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Comentários (opcional)</label>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={actionType === 'approve' ? 'Aprovado conforme procedimento...' : 'Motivo da rejeição...'}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleAction}
              disabled={isSubmitting}
              variant={actionType === 'approve' ? 'default' : 'destructive'}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {actionType === 'approve' ? 'Aprovar' : 'Rejeitar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
